import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { TopNav } from "@/components/TopNav";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Check, Zap, Crown, Sparkles } from "lucide-react";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ plans: [], credit_packs: [] });
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/pricing").then((r) => setData(r.data)); }, []);

  const subscribe = async (plan) => {
    if (!user) { nav("/login"); return; }
    if (["admin", "super_admin"].includes(user.role)) {
      toast.info("Admins are on a permanent complimentary Pro Studio plan.");
      return;
    }
    const lookup = annual ? plan.annual_lookup : plan.monthly_lookup;
    setBusy(plan.id);
    try {
      const r = await api.post("/billing/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout"); setBusy(null);
    }
  };

  const buyPack = async (pack) => {
    if (!user) { nav("/login"); return; }
    setBusy(pack.id);
    try {
      const r = await api.post("/billing/checkout", { lookup_key: pack.lookup, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error("Could not start checkout"); setBusy(null); }
  };

  const icons = { starter: Zap, creator: Sparkles, pro_studio: Crown };

  return (
    <div className="min-h-screen">
      <TopNav />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="label-mono text-cyan mb-3">Plans & credits</p>
          <h1 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl text-slate-50">Pay for power, not promises.</h1>
          <p className="text-slate-400 mt-5">Every plan includes recurring credits. AI production is always metered, you see the quote before anything runs.</p>
          <div className="inline-flex items-center gap-1 mt-8 p-1 rounded-full bg-white/5 border border-white/10">
            <button data-testid="billing-monthly" onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${!annual ? "bg-cyan-400 text-black" : "text-slate-300"}`}>Monthly</button>
            <button data-testid="billing-annual" onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${annual ? "bg-cyan-400 text-black" : "text-slate-300"}`}>Annual <span className="text-xs opacity-70">−20%</span></button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {data.plans.map((p, i) => {
            const Icon = icons[p.id] || Zap;
            const price = annual ? p.annual_price : p.monthly_price;
            return (
              <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                data-testid={`plan-card-${p.id}`}
                className={`rounded-2xl p-8 relative ${p.highlight ? "tracing-border glow-cyan" : "card-surface"}`}>
                {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 label-mono px-3 py-1 rounded-full bg-cyan-400 text-black">Most popular</span>}
                <Icon className={`w-7 h-7 mb-4 ${p.highlight ? "text-cyan" : "text-violet"}`} />
                <h3 className="font-display font-bold text-2xl text-slate-50">{p.name}</h3>
                <p className="text-slate-400 text-sm mt-1.5 h-10">{p.public_description}</p>
                <div className="mt-6 flex items-end gap-1.5">
                  <span className="font-display font-extrabold text-4xl text-slate-50">${price}</span>
                  <span className="text-slate-500 mb-1.5">/{annual ? "yr" : "mo"}</span>
                </div>
                <p className="text-cyan text-sm font-mono-a mt-2">{p.monthly_credits.toLocaleString()} credits / mo</p>
                <Button data-testid={`subscribe-btn-${p.id}`} onClick={() => subscribe(p)} disabled={busy === p.id}
                  className={`w-full mt-6 rounded-full h-11 font-semibold ${p.highlight ? "bg-cyan-400 text-black hover:bg-cyan-300" : "bg-white/10 text-slate-50 hover:bg-white/15"}`}>
                  {busy === p.id ? "Redirecting…" : "Subscribe"}
                </Button>
                <ul className="mt-7 space-y-3">
                  {[`Up to ${p.limits.max_projects} projects`, `${p.limits.max_devices} device(s)`, `Export: ${p.limits.max_export_quality}`, `${p.limits.support} support`].map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                      <Check className="w-4 h-4 text-cyan shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-20">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="label-mono text-amber mb-2">Top up anytime</p>
              <h2 className="font-display font-bold text-2xl text-slate-50">Prepaid credit packs</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {data.credit_packs.map((pk) => (
              <div key={pk.id} data-testid={`pack-card-${pk.id}`} className="card-surface rounded-xl p-6 flex items-center justify-between hover:border-amber-400/40 transition-colors">
                <div>
                  <p className="font-display font-semibold text-lg text-slate-50">{pk.name}</p>
                  <p className="text-amber font-mono-a text-sm mt-1">{pk.credits.toLocaleString()} credits</p>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold text-2xl text-slate-50">${pk.price}</p>
                  <Button data-testid={`buy-pack-btn-${pk.id}`} onClick={() => buyPack(pk)} disabled={busy === pk.id} size="sm"
                    className="mt-2 rounded-full bg-amber-400 text-black hover:bg-amber-300 font-semibold">Buy</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
